// Menu Vendas → Pré-contratos (pedido 2026-10-05). Todo envio de formulário
// (Administração → Formulários) vira um pré-cadastro aqui; o vendedor
// completa os dados, cria o usuário (cliente) e só então gera o contrato.
// Backend: src/sales (permissão "vendas", admin sempre, cliente nunca).
import { gpsDriveDelete, gpsDriveGet, gpsDrivePost, gpsDrivePut } from "@/lib/api/gpsdrive-client";
import type { FormAnswer } from "@/services/gpsdrive-forms.service";

export interface SalesAccess {
  view: boolean;
  edit: boolean;
  delete: boolean;
}

export interface LeadVehicle {
  plate: string;
  model: string;
  year: string;
  color: string;
  /** Cada veículo com seu plano e mensalidade (2026-10-15). */
  plan: string;
  monthlyValue: number | null;
}

export interface LeadData {
  name: string;
  email: string;
  cpfCnpj: string;
  rg: string;
  birthDate: string;
  phone1: string;
  phone2: string;
  cep: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  authorizedPeople: string[];
  plan: string;
  planValue: number | null;
  dueDay: string;
  paymentMethod: string;
  vehicles: LeadVehicle[];
  notes: string;
}

export type LeadStatus = "novo" | "em_contato" | "perdido";
export type LeadStage = LeadStatus | "usuario_criado" | "contrato_enviado" | "assinado";

export interface SalesLead {
  id: string;
  source: "form" | "manual" | "sheet";
  status: LeadStatus;
  stage: LeadStage;
  data: LeadData;
  formId: string | null;
  formTitle: string | null;
  answers: FormAnswer[];
  user: { id: string; traccarUserId: number | null; name: string; email: string } | null;
  contract: { templateId: string | null; signingToken: string | null; signedAt: string | null } | null;
  /** Quando foi gravado na planilha Google (Vendas → Planilha). */
  sheetSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const STAGE_LABELS: Record<LeadStage, string> = {
  novo: "Novo",
  em_contato: "Em contato",
  perdido: "Perdido",
  usuario_criado: "Cliente criado",
  contrato_enviado: "Contrato enviado",
  assinado: "Assinado",
};

export function blankLeadData(): LeadData {
  return {
    name: "",
    email: "",
    cpfCnpj: "",
    rg: "",
    birthDate: "",
    phone1: "",
    phone2: "",
    cep: "",
    street: "",
    number: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "",
    authorizedPeople: [],
    plan: "",
    planValue: null,
    dueDay: "",
    paymentMethod: "",
    vehicles: [],
    notes: "",
  };
}

// Vendas → Planilha Google (2026-10-14). A planilha roda um Apps Script
// (salesSheetScript) publicado como App da Web; o backend lê as colunas da
// linha 1 e acrescenta uma linha por cadastro, coluna a coluna (mapping).
export interface SheetColumnMap {
  column: string;
  source: string;
}

export interface SalesSheetConfig {
  token: string;
  url: string | null;
  sheetName: string | null;
  mapping: SheetColumnMap[];
  enabled: boolean;
  startedAt: string | null;
  lastSentAt: string | null;
  lastError: string | null;
  /** Cadastros do formulário desde que ligou, ainda não enviados. */
  pendingNew: number;
  /** Cadastros de antes de ligar o envio, nunca enviados. */
  olderUnsent: number;
}

export interface SalesSheetInfo {
  name: string;
  sheets: { name: string; headers: string[] }[];
}

// Colado pelo usuário em Extensões → Apps Script da própria planilha. Escreve
// só nas colunas mapeadas (não pisa em colunas com fórmula) e como texto
// (CPF/telefone/CEP não perdem zero à esquerda).
export function salesSheetScript(token: string): string {
  return `// GPS Drive → Vendas. Não altere o TOKEN.
var TOKEN = "${token}";

function doGet(e) {
  if (!e || !e.parameter || e.parameter.token !== TOKEN) return out_({ ok: false, error: "Token inválido — copie o script de novo no GPS Drive" });
  var ss = SpreadsheetApp.getActiveSpreadsheet();
  if (e.parameter.action === "rows") {
    var sh = ss.getSheetByName(e.parameter.sheet);
    if (!sh) return out_({ ok: false, error: "A aba '" + e.parameter.sheet + "' não existe mais na planilha" });
    var lastRow = sh.getLastRow(), lastCol = sh.getLastColumn();
    if (!lastRow || !lastCol) return out_({ ok: true, headers: [], rows: [] });
    var all = sh.getRange(1, 1, lastRow, lastCol).getDisplayValues();
    return out_({ ok: true, headers: all[0], rows: all.slice(1) });
  }
  var sheets = ss.getSheets().map(function (s) {
    var cols = s.getLastColumn();
    return { name: s.getName(), headers: cols ? s.getRange(1, 1, 1, cols).getDisplayValues()[0] : [] };
  });
  return out_({ ok: true, name: ss.getName(), sheets: sheets });
}

function doPost(e) {
  var body = JSON.parse(e.postData.contents);
  if (body.token !== TOKEN) return out_({ ok: false, error: "Token inválido — copie o script de novo no GPS Drive" });
  var lock = LockService.getScriptLock();
  lock.waitLock(30000);
  try {
    var s = SpreadsheetApp.getActiveSpreadsheet().getSheetByName(body.sheet);
    if (!s) return out_({ ok: false, error: "A aba '" + body.sheet + "' não existe mais na planilha" });
    var rows = body.rows || [];
    if (!rows.length) return out_({ ok: true, appended: 0 });
    var headers = s.getRange(1, 1, 1, Math.max(s.getLastColumn(), 1)).getDisplayValues()[0];
    var start = s.getLastRow() + 1;
    headers.forEach(function (h, i) {
      h = String(h).trim();
      if (!h || !rows.some(function (r) { return r.hasOwnProperty(h); })) return;
      var values = rows.map(function (r) { return [r.hasOwnProperty(h) ? String(r[h]) : ""]; });
      s.getRange(start, i + 1, rows.length, 1).setNumberFormat("@").setValues(values);
    });
    return out_({ ok: true, appended: rows.length });
  } finally {
    lock.releaseLock();
  }
}

function out_(o) {
  return ContentService.createTextOutput(JSON.stringify(o)).setMimeType(ContentService.MimeType.JSON);
}
`;
}

const BASE = "/api/v1/sales";
const enc = encodeURIComponent;

export interface SheetImportResult {
  dryRun: boolean;
  rows: number;
  clients: number;
  created: number;
  updated: number;
  unchanged: number;
  vehiclesAdded: number;
}

export const salesService = {
  access: () => gpsDriveGet<SalesAccess>(`${BASE}/access`),
  list: () => gpsDriveGet<SalesLead[]>(`${BASE}/leads`),
  create: (data: LeadData) => gpsDrivePost<SalesLead>(`${BASE}/leads`, { data }),
  update: (id: string, patch: { status?: LeadStatus; data?: LeadData }) =>
    gpsDrivePut<SalesLead>(`${BASE}/leads/${enc(id)}`, patch),
  linkUser: (id: string, traccarUserId: number) =>
    gpsDrivePost<SalesLead>(`${BASE}/leads/${enc(id)}/link-user`, { traccarUserId }),
  remove: (id: string) => gpsDriveDelete<{ ok: true }>(`${BASE}/leads/${enc(id)}`),
  sendToSheet: (id: string) => gpsDrivePost<SalesLead>(`${BASE}/leads/${enc(id)}/sheet`),
  sheet: () => gpsDriveGet<SalesSheetConfig>(`${BASE}/sheet`),
  inspectSheet: (url: string) => gpsDrivePost<SalesSheetInfo>(`${BASE}/sheet/inspect`, { url }),
  saveSheet: (dto: { url: string; sheetName: string; mapping: SheetColumnMap[]; enabled: boolean }) =>
    gpsDrivePut<SalesSheetConfig>(`${BASE}/sheet`, dto),
  removeSheet: () => gpsDriveDelete<{ ok: true }>(`${BASE}/sheet`),
  sendOlderToSheet: () => gpsDrivePost<{ sent: number }>(`${BASE}/sheet/send-older`),
  // "Puxar da planilha" (2026-10-15): junta as linhas por cliente. dryRun só conta.
  importFromSheet: (dryRun: boolean) =>
    gpsDrivePost<SheetImportResult>(`${BASE}/sheet/import${dryRun ? "?dryRun=1" : ""}`),
};
